import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  UsersIcon,
  ServerIcon,
  ClipboardDocumentListIcon,
  CogIcon,
  CalendarIcon,
  DocumentChartBarIcon,
  RocketLaunchIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

/**
 * Settings Page
 * Modern tile-based layout for accessing all system sections
 */

function SettingsPage({ user }) {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'Admin';
  const isManager = user?.role === 'Manager' || isAdmin;

  const tiles = [
    {
      id: 'companies',
      title: 'Companies',
      description: 'Manage client companies and onboarding',
      icon: BuildingOfficeIcon,
      color: 'from-blue-500 to-blue-600',
      path: '/companies',
      show: true,
    },
    {
      id: 'setup',
      title: 'Company Setup',
      description: 'Create new company with guided wizard',
      icon: RocketLaunchIcon,
      color: 'from-purple-500 to-purple-600',
      path: '/journey',
      show: true,
    },
    {
      id: 'planning',
      title: 'Planning',
      description: 'Schedule and track project timelines',
      icon: CalendarIcon,
      color: 'from-green-500 to-green-600',
      path: '/planning',
      show: isManager,
    },
    {
      id: 'reports',
      title: 'Reports',
      description: 'View onboarding progress and analytics',
      icon: DocumentChartBarIcon,
      color: 'from-amber-500 to-amber-600',
      path: '/reports',
      show: true,
    },
    {
      id: 'users',
      title: 'Users',
      description: 'Manage system users and permissions',
      icon: UsersIcon,
      color: 'from-pink-500 to-pink-600',
      path: '/users',
      show: isAdmin,
    },
    {
      id: 'services',
      title: 'Services',
      description: 'Monitor system services and health',
      icon: ServerIcon,
      color: 'from-teal-500 to-teal-600',
      path: '/admin/services',
      show: isAdmin,
    },
    {
      id: 'audit',
      title: 'Audit Log',
      description: 'View system activity and changes',
      icon: ClipboardDocumentListIcon,
      color: 'from-indigo-500 to-indigo-600',
      path: '/admin/audit-log',
      show: isAdmin,
    },
    {
      id: 'management',
      title: 'Management Console',
      description: 'Advanced system configuration',
      icon: CogIcon,
      color: 'from-gray-600 to-gray-700',
      path: '/admin/management-console',
      show: isAdmin,
    },
    {
      id: 'downloads',
      title: 'Downloads',
      description: 'Download mobile apps and resources',
      icon: ArrowDownTrayIcon,
      color: 'from-cyan-500 to-cyan-600',
      path: '/downloads',
      show: true,
    },
  ];

  const visibleTiles = tiles.filter(tile => tile.show);

  function SettingTile({ tile }) {
    const Icon = tile.icon;
    
    return (
      <button
        onClick={() => navigate(tile.path)}
        className="group relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 text-left overflow-hidden"
      >
        {/* Background gradient on hover */}
        <div className={`absolute inset-0 bg-gradient-to-br ${tile.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
        
        {/* Content */}
        <div className="relative z-10">
          {/* Icon */}
          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tile.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="w-7 h-7 text-white" />
          </div>
          
          {/* Title */}
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {tile.title}
          </h3>
          
          {/* Description */}
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {tile.description}
          </p>
        </div>
        
        {/* Arrow indicator */}
        <div className="absolute top-6 right-6 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 sm:p-8 lg:p-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3">
            Settings
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Configure and manage your MiBOT Onboarding system
          </p>
        </div>

        {/* User Info Card */}
        <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-6 mb-8 text-white shadow-lg">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-2xl font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <h2 className="text-2xl font-bold">{user?.name || 'User'}</h2>
              <p className="text-white/80">{user?.email || ''}</p>
              <div className="inline-block mt-1 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold">
                {user?.role || 'User'}
              </div>
            </div>
          </div>
        </div>

        {/* Tiles Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleTiles.map((tile) => (
            <SettingTile key={tile.id} tile={tile} />
          ))}
        </div>

        {/* Footer Info */}
        <div className="mt-12 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>MiBOT Onboarding System v1.0</p>
          <p className="mt-1">© 2026 MiBOT. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;

